import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Wallet, Landmark, CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AffiliatePayouts() {
  const [rows, setRows] = useState([]);
  const [connect, setConnect] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const loadStatus = () =>
    api.get("/affiliate/connect/status")
      .then((r) => setConnect(r.data))
      .catch(() => setConnect({ onboarded: false, payouts_enabled: false, unavailable: true }));

  useEffect(() => {
    api.get("/affiliate/payouts").then((r) => setRows(r.data));
    loadStatus();
  }, []);

  useEffect(() => {
    if (params.get("connect_return") || params.get("connect_refresh")) {
      loadStatus();
      params.delete("connect_return"); params.delete("connect_refresh");
      setParams(params, { replace: true });
      toast.success("Onboarding complete — refreshing your status…");
    }
  }, [params, setParams]);

  const startOnboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/affiliate/connect/onboard", {
        return_url: `${window.location.origin}/affiliate/payouts`,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start onboarding");
      setBusy(false);
    }
  };

  const total = rows.reduce((a, r) => a + (r.amount || 0), 0);

  return (
    <div data-testid="affiliate-payouts">
      <div className="pf-chip pf-chip-mint"><Wallet className="w-3 h-3" /> Payouts</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Payouts</h2>
      <p className="text-sm text-slate-600 mt-1 max-w-xl">
        Lifetime paid: <span className="font-semibold text-cyan-700">${total.toFixed(2)}</span>
      </p>

      {/* Stripe Connect onboarding card */}
      <div className="pf-card p-6 mt-6" data-testid="connect-card">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-cyan-100 to-emerald-100">
            <Landmark className="w-5 h-5 text-cyan-700" />
          </div>
          <div className="flex-1">
            <div className="font-display text-xl text-slate-900">Bank payouts via Stripe</div>
            <p className="text-sm text-slate-600 mt-1">
              Onboard a free Stripe Express account to receive earnings directly to your bank —
              faster and safer than manual transfers.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {connect?.unavailable ? (
                <span className="pf-chip" data-testid="connect-status-unavailable">
                  <AlertCircle className="w-3 h-3" /> Stripe Connect not configured yet
                </span>
              ) : connect?.payouts_enabled ? (
                <span className="pf-chip pf-chip-mint" data-testid="connect-status-ready">
                  <CheckCircle2 className="w-3 h-3" /> Payouts enabled
                </span>
              ) : connect?.onboarded ? (
                <span className="pf-chip pf-chip-lav" data-testid="connect-status-pending">
                  Under Stripe review — {connect?.requirements?.length || 0} field(s) outstanding
                </span>
              ) : (
                <span className="pf-chip" data-testid="connect-status-none">Not onboarded</span>
              )}
              {!connect?.payouts_enabled && !connect?.unavailable && (
                <button
                  onClick={startOnboard}
                  disabled={busy}
                  className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                  data-testid="connect-onboard-btn"
                >
                  {busy ? "Redirecting…" : "Set up bank payouts"} <ExternalLink className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="pf-card overflow-hidden mt-6">
        <table className="w-full text-sm">
          <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-5 py-3">Date</th>
              <th className="px-5 py-3">Method</th>
              <th className="px-5 py-3">Amount</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-white/60" data-testid={`aff-payout-${r.id}`}>
                <td className="px-5 py-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-3 text-xs">
                  {r.method === "stripe_connect" ? (
                    <span className="pf-chip pf-chip-mint">Stripe Connect</span>
                  ) : (
                    r.method
                  )}
                </td>
                <td className="px-5 py-3 tabular-nums font-semibold text-slate-900">${r.amount.toFixed(2)}</td>
                <td className="px-5 py-3"><span className="pf-chip pf-chip-mint">{r.status}</span></td>
                <td className="px-5 py-3 text-xs text-slate-500">{r.note || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500">No payouts issued yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
